// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Anomaly detail. Modelled on AlarmDetail and sharing its stylesheet, because
// anomalies (findings upstream) are the same `aem.alarm_event_state` row shape as
// alarms — only `event_type` differs.

import React, { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ActionableNotification, Button } from '@carbon/react';
import { ArrowLeft } from '@carbon/icons-react';
import { useAnomaly } from '../../hooks/useAnomaly';
import { useActiveSite } from '../../hooks/useActiveSite';
import { useAuth } from '../../hooks/useAuth';
import { useShellStore } from '../../stores/shellStore';
import { DxTag, Loading, SevTag } from '../../components/shell/ScreenHelpers';
import AlarmTelemetry from '../../components/shell/AlarmTelemetry';
import InlineEquipmentView from '../../components/equipment/InlineEquipmentView';
import AssetLineupPanel from '../../components/equipment/AssetLineupPanel';
import { locationShort } from '../../utils/location';
import AlarmBriefCard from './AlarmBriefCard';
import AlarmMetaGrid from './AlarmMetaGrid';
import { equipmentTypeDisplayName } from '../../utils/equipmentTypeLabels';
import AlarmNextStepCard from './AlarmNextStepCard';
import AnomalyPromoteCard from './AnomalyPromoteCard';
import AnomalySopList from './AnomalySopList';
import { StaleDataNotice } from '../../components/common/StaleDataNotice';
import './AlarmDetail.css';
import { alarmDetailPath } from '../../services/alarmSelectors';
import { useFlagOn } from '../../hooks/useFlagOn';

export default function AnomalyDetail() {
  const { alarmCode } = useParams();
  const [searchParams] = useSearchParams();
  const { siteId: activeSiteId } = useActiveSite();
  const { sites } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Same flag as the alarm's Response procedure card: both show the SOPs bound to the rule.
  const responseProcedureEnabled = useFlagOn('alarmResponseProcedureEnabled');
  const setPageAlarmContext = useShellStore(s => s.setPageAlarmContext);

  const siteId = searchParams.get('site') ?? activeSiteId;
  const equipmentId = searchParams.get('equipment') ?? undefined;
  const alarmId = searchParams.get('alarm_id') ?? undefined;

  const { anomaly, isLoading, isError, error, refetch } = useAnomaly(
    siteId,
    alarmCode,
    equipmentId,
    alarmId
  );

  useEffect(() => {
    if (!anomaly) return;
    setPageAlarmContext({
      id: anomaly.id,
      alarm_id: anomaly.alarmId,
      rule: anomaly.ruleId,
      asset: anomaly.asset,
      site: locationShort(anomaly.location),
      ranked_causes: [],
      bound_sops: anomaly.sopIds,
      triage_summary: anomaly.diagnosis,
      status: anomaly.status,
      assigned_to: anomaly.owner,
      equipment_type: anomaly.group,
      rule_title: anomaly.ruleTitle,
      category: anomaly.category,
      priority: anomaly.priority,
      control_layer: anomaly.controlLayer,
      event_type: 'anomaly',
    });
    return () => setPageAlarmContext(null);
  }, [anomaly, sites, setPageAlarmContext]);

  if (isLoading) return <Loading />;

  // "Not found" only for a genuine empty result; a failed request is retryable.
  if (isError && !anomaly) {
    return (
      <div className="hs-page">
        <ActionableNotification
          kind="error"
          lowContrast
          title="Couldn't load this anomaly"
          subtitle={error?.message ?? 'Request failed'}
          actionButtonLabel="Retry"
          onActionButtonClick={() => refetch()}
          hideCloseButton
        />
        <Button kind="ghost" onClick={() => navigate('/home/anomalies')}>
          Back to anomalies
        </Button>
      </div>
    );
  }

  if (!anomaly) {
    return (
      <div className="hs-page">
        <p>Anomaly not found.</p>
        <Button kind="ghost" onClick={() => navigate('/home/anomalies')}>
          Back to anomalies
        </Button>
      </div>
    );
  }

  return (
    <div className="hs-page ald-page">
      <button type="button" className="ald-back" onClick={() => navigate(-1)}>
        <ArrowLeft size={16} /> Back to list
      </button>

      {isError && <StaleDataNotice what="this anomaly" error={error} onRetry={() => refetch()} />}
      {anomaly.ruleUnavailable && (
        <StaleDataNotice
          title="Couldn't load this anomaly's rule"
          subtitle={
            responseProcedureEnabled
              ? 'Severity, source and linked procedures may be incomplete.'
              : 'Severity and source may be incomplete.'
          }
          onRetry={() => refetch()}
        />
      )}

      <div className="ald-head">
        <div>
          <div className="hs-card-row" style={{ marginBottom: 8 }}>
            <SevTag
              sev={anomaly.sev}
              label={
                anomaly.ruleUnavailable && anomaly.sev === 'Unknown'
                  ? 'Severity unavailable'
                  : undefined
              }
            />
            {/* Same chip as AlarmDetail. A null alarm_status means AEM has not
                reported one (#583): say so rather than hide the chip, since the
                closed_loop reset state would otherwise read as finished. */}
            <DxTag>{anomaly.statusChip ?? 'No status yet'}</DxTag>
            {anomaly.siteId && (
              <span className="ald-site">
                {sites.find(s => s.site_id === anomaly.siteId)?.label ?? anomaly.siteId}
              </span>
            )}
          </div>
          <h1 className="hs-title">{anomaly.asset}</h1>
          <p className="ald-issue">{anomaly.issue}</p>
        </div>
      </div>

      <AlarmMetaGrid
        alarm={anomaly}
        // Human labels only: the raw `equipment_type` / `alarm_status` enums are
        // what AB#35958 / AB#36229 removed from this row.
        extra={[
          ['Anomaly code', anomaly.id],
          ['Equipment type', anomaly.group ? equipmentTypeDisplayName(anomaly.group) : undefined],
          ['Point', anomaly.point],
          ['Status', anomaly.statusChip],
          ['Assigned', anomaly.owner],
        ]}
      />

      <div className="ald-grid">
        <div className="ald-col-main">
          <AlarmBriefCard alarm={anomaly} eventType="anomaly" />

          {responseProcedureEnabled && (
            <AnomalySopList
              sops={anomaly.sops ?? []}
              unavailable={anomaly.ruleUnavailable}
              siteId={anomaly.siteId}
            />
          )}

          <AlarmTelemetry siteId={anomaly.siteId} equipmentId={anomaly.assetId} />
        </div>

        <div className="ald-col-side">
          {anomaly.siteId && (
            <AlarmNextStepCard
              alarm={anomaly}
              siteId={anomaly.siteId}
              machine="closed_loop"
              finding
              // apply-event answers 204, so re-read the finding; the list's chip follows.
              onEventApplied={() => {
                queryClient.invalidateQueries({ queryKey: ['anomalies', anomaly.siteId] });
                return refetch();
              }}
            />
          )}

          <AnomalyPromoteCard
            alarmId={anomaly.alarmId}
            alarmCode={anomaly.id}
            siteId={anomaly.siteId}
            status={anomaly.status}
            onPromoted={() =>
              navigate(alarmDetailPath(anomaly), {
                replace: true,
                state: { backTo: '/home/alarms' },
              })
            }
          />

          <InlineEquipmentView
            equipmentId={anomaly.assetId ?? ''}
            siteId={anomaly.siteId}
            heading="Power Flow"
            className="ald-card"
          />

          <AssetLineupPanel
            equipmentId={anomaly.assetId ?? ''}
            siteId={anomaly.siteId}
            className="ald-card"
          />
        </div>
      </div>
    </div>
  );
}
